import { h, Host } from "@stencil/core";
import { checkFileType } from "./utils/checkFileType";
let fileInputIdCounter = 0;
export class FileUpload {
    constructor() {
        this.maxFiles = 3;
        this.maxFileSize = 20;
        this.acceptedFiles = "image/*,application/pdf";
        this.messageMaxFilesReached = "Upload limit reached";
        this.messageMaxFileSize = "One of the uploaded files was too big";
        this.messageInvalidFile = "One of the uploaded files did not have the right file extension";
        this.errorMsg = "";
        this.label = "";
        this.disabled = false;
        this.required = false;
        this.error = false;
        this.name = "";
        this.bulkDelete = false;
        this.message = "";
        this.uploadedFilesCount = 0;
    }
    element;
    baseClass = "ux-file-upload";
    fileUploadField = null;
    fileContainer = null;
    fileInputIdentifier = "data-file-input-id";
    effectiveAllowedSize;
    allowedFileTypes;
    /** Emitted when files are added or removed */
    uxFileChange;
    componentWillRender() {
        this.effectiveAllowedSize = this.maxFileSize * 1024 * 1024;
        this.allowedFileTypes = this.acceptedFiles.split(",");
    }
    connectedCallback() {
        this.element.addEventListener("change", this.onInputChange);
        this.element.addEventListener("dragenter", this.onDragEnter);
        this.element.addEventListener("dragleave", this.onDragLeave);
        this.element.addEventListener("dragover", this.stopEvent);
        this.element.addEventListener("drop", this.handleDrop);
    }
    onDragLeave = (e) => {
        this.stopEvent(e);
    };
    onDragEnter = (e) => {
        this.stopEvent(e);
    };
    stopEvent = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };
    handleDrop = (e) => {
        this.stopEvent(e);
        const dt = e.dataTransfer;
        const files = dt?.files || new FileList();
        this.checkAndAddFiles(files);
    };
    collectFiles = () => {
        const filesArray = [];
        if (!this.fileContainer) {
            return filesArray;
        }
        this.fileContainer.querySelectorAll(`input[type="file"]`).forEach((input) => {
            if (input.files) {
                for (const file of input.files) {
                    filesArray.push(file);
                }
            }
        });
        return filesArray;
    };
    emitChangeEvent = () => {
        this.uxFileChange.emit({ files: this.collectFiles() });
    };
    onInputChange = () => {
        const files = this.fileUploadField?.files || new FileList();
        this.checkAndAddFiles(files);
        this.emitChangeEvent();
    };
    resetFileUploadField = () => {
        if (this.fileUploadField) {
            this.fileUploadField.files = null;
            this.fileUploadField.value = "";
        }
    };
    checkAndAddFiles = (fileList) => {
        this.checkAndAddIndividualFiles(fileList);
        this.resetFileUploadField();
        this.updateUploadedFilesCount();
    };
    checkAndAddIndividualFiles = (fileList) => {
        if (!this.fileContainer) {
            return;
        }
        for (const file of fileList) {
            if (this.maxFiles === this.uploadedFilesCount) {
                this.error = true;
                this.errorMsg = this.messageMaxFilesReached;
                throw new Error(`UxFileUpload: Too many files uploaded. Max files allowed is ${this.maxFiles}`);
            }
            else if (file.size > this.effectiveAllowedSize) {
                this.error = true;
                this.errorMsg = this.messageMaxFileSize;
                throw new Error("UxFileUpload: File size too big");
            }
            else if (!checkFileType(file, this.allowedFileTypes)) {
                this.error = true;
                this.errorMsg = this.messageInvalidFile;
                throw new Error("UxFileUpload: Invalid file extension");
            }
            else {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                const newFileIput = document.createElement("input");
                newFileIput.setAttribute("type", "file");
                newFileIput.setAttribute(this.fileInputIdentifier, String(fileInputIdCounter++));
                newFileIput.setAttribute("name", this.name);
                newFileIput.files = dataTransfer.files;
                this.fileContainer.insertAdjacentElement("beforeend", newFileIput);
                this.updateUploadedFilesCount();
            }
        }
    };
    updateUploadedFilesCount = () => {
        let totalFiles = 0;
        const fileInputs = this.fileContainer?.querySelectorAll("input") || [];
        if (fileInputs.length === 0) {
            this.uploadedFilesCount = 0;
        }
        else {
            fileInputs.forEach((file) => {
                if (file.files) {
                    totalFiles += file.files.length;
                }
            });
        }
        this.uploadedFilesCount = totalFiles;
    };
    renderThumbnails = () => {
        if (this.fileContainer === null) {
            return null;
        }
        const thumbnails = [];
        const inputs = this.fileContainer.querySelectorAll("input");
        if (inputs.length === 0) {
            return null;
        }
        for (const input of inputs) {
            const files = input.files || new FileList();
            for (const file of files) {
                thumbnails.push(h("div", { class: {
                        [`${this.baseClass}__thumbnail-container`]: true
                    } }, h("ux-file-upload-thumbnail", { key: file.name, "file-type": file.type, file: file, "is-individual": true, "file-input-id": input.getAttribute(this.fileInputIdentifier), "file-name": file.name })));
            }
        }
        if (thumbnails.length === 0) {
            return null;
        }
        return thumbnails;
    };
    deleteInput(e) {
        if (this.fileContainer) {
            const inputToDelete = this.fileContainer.querySelector(`input[${this.fileInputIdentifier}="${e.detail.fileInputId}"]`);
            if (inputToDelete === null) {
                return;
            }
            inputToDelete.remove();
        }
        else {
            if (this.fileUploadField) {
                this.fileUploadField.value = "";
            }
        }
        const filesCount = this.uploadedFilesCount;
        this.updateUploadedFilesCount();
        if (this.uploadedFilesCount !== filesCount) {
            this.emitChangeEvent();
        }
    }
    resetErrorOnBlur(e) {
        const eTarget = e.target;
        if (eTarget.closest("ux-file-upload") !== this.element) {
            return;
        }
        this.error = false;
    }
    /** Remove all files and reset inputs */
    async reset() {
        this.deleteAllFiles();
    }
    deleteAllFiles = (e) => {
        if (e) {
            e.preventDefault();
        }
        const filesCount = this.uploadedFilesCount;
        this.resetFileUploadField();
        if (this.fileContainer) {
            const fileInputs = this.fileContainer.querySelectorAll("input");
            if (fileInputs.length === 0) {
                return;
            }
            fileInputs.forEach((fileInput) => fileInput.remove());
        }
        this.updateUploadedFilesCount();
        if (this.uploadedFilesCount !== filesCount) {
            this.emitChangeEvent();
        }
    };
    checkItemsPerRow = () => {
        return getComputedStyle(this.element).getPropertyValue("--ux-file-upload-items-per-row");
    };
    render() {
        const { label, disabled, required, error, errorMsg } = this;
        return (h(Host, { key: 'a75ffc73d581b5fddd24e066db6d543f73d5fca1', class: {
                [`${this.baseClass}`]: true,
                [`${this.baseClass}--disabled`]: disabled,
                [`${this.baseClass}--error`]: error
            } }, (label !== undefined || (error && errorMsg !== "")) && (h("div", { key: '34c3ca817261b81c1ddabcc3e3602cee6892a2aa', class: "ux-label-container" }, label !== "" && (h("label", { key: 'd123ae0f105ee1bd380efa2bdc336a6a3eb428b1', class: "ux-label" }, label, required && h("span", { key: 'a3b7b5336c619c4d134695b0c82cfa59c5598a20' }, " *"))), " ", error && errorMsg !== "" && h("span", { key: '0888fe2dea1003c085dee6958d789fc9fa67b76d', class: `ux-label ux-label--message ux-label--error` }, errorMsg))), h("div", { key: '15fef07f7d8928b24087a32e7db16c06b4f4f47e', class: `${this.baseClass}__file-container`, ref: (el) => (this.fileContainer = el || null) }), h("div", { key: '11cb4826f1f312184d9dcc557c5a18ce89cad95e', class: {
                [`${this.baseClass}__container`]: true,
                [`${this.baseClass}__container--bulk-delete`]: this.bulkDelete,
                [`${this.baseClass}__container--items-per-row`]: this.checkItemsPerRow() !== "auto"
            } }, h(this.renderThumbnails), this.uploadedFilesCount < this.maxFiles && (h("div", { key: 'b13ee5a695c0387126e6fde1e221f44a9ae6cd46', class: {
                [`${this.baseClass}__action-container`]: true
            } }, h("div", { key: '011c5cf348363407cb988b574c9eba656f91e31f', class: {
                [`${this.baseClass}__action-container-inner`]: true
            } }, h("div", { key: 'ee5f609a45a65ff8b22a31ff5d529c3f6b3289f5', class: {
                [`${this.baseClass}__add-file`]: true,
                [`${this.baseClass}__add-file--with-thumbnails`]: this.uploadedFilesCount !== 0
            } }, h("ux-icon", { key: '7bb9d1db67134ae075e5f1c9a6ea44135546e0a3', name: "cross", weight: "normal" })), this.uploadedFilesCount !== 0 && this.bulkDelete && (h("button", { key: '8ee19aabdc5d234e0296560c131d7bc2a7d15ded', class: `${this.baseClass}__delete-all`, onClick: this.deleteAllFiles }, h("ux-icon", { key: 'cb0ad623e4e89e3e77fcf86c27f859520ce2f90e', name: "bin", weight: "normal" })))))), h("label", { key: 'f726b766298f4433c30bad45135aa955d915ad90', class: `${this.baseClass}__label` }, h("input", { key: '6772f98e65940b93f4c983a4ae97d21697f209c0', type: "file", multiple: this.maxFiles > 1 ? true : undefined, class: `${this.baseClass}__input-field`, ref: (el) => (this.fileUploadField = el || null), onFocus: () => (this.error = false), required: this.required && !this.uploadedFilesCount, "aria-required": this.required, "aria-invalid": error ? "true" : "false", accept: this.acceptedFiles })))));
    }
    static get is() { return "ux-file-upload"; }
    static get originalStyleUrls() {
        return {
            "$": ["file-upload.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["file-upload.css"]
        };
    }
    static get properties() {
        return {
            "maxFiles": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Option to send multiple files in one request"
                },
                "attribute": "max-files",
                "reflect": false,
                "defaultValue": "3"
            },
            "maxFileSize": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The maximum upload file size in MB"
                },
                "attribute": "max-file-size",
                "reflect": false,
                "defaultValue": "20"
            },
            "acceptedFiles": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Accepted file types in comma separated list of mime types or file extensions"
                },
                "attribute": "accepted-files",
                "reflect": false,
                "defaultValue": "\"image/*,application/pdf\""
            },
            "messageMaxFilesReached": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Message Upload limit reached"
                },
                "attribute": "message-max-files-reached",
                "reflect": false,
                "defaultValue": "\"Upload limit reached\""
            },
            "messageMaxFileSize": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Message Upload limit reached"
                },
                "attribute": "message-max-file-size",
                "reflect": false,
                "defaultValue": "\"One of the uploaded files was too big\""
            },
            "messageInvalidFile": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Message invalid file extension"
                },
                "attribute": "message-invalid-file",
                "reflect": false,
                "defaultValue": "\"One of the uploaded files did not have the right file extension\""
            },
            "errorMsg": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "generic error message to be displayed"
                },
                "attribute": "error-msg",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Label text describing the file upload field"
                },
                "attribute": "label",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "indicates if the file upload is disabled"
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "required": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "indicates if the file upload is required"
                },
                "attribute": "required",
                "reflect": false,
                "defaultValue": "false"
            },
            "error": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "indicates if the file upload has an error state. Use error attribute only for static error rendering."
                },
                "attribute": "error",
                "reflect": false,
                "defaultValue": "false"
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "name of the input field"
                },
                "attribute": "name",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "bulkDelete": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "indicates if component has bulk delete functionality"
                },
                "attribute": "bulk-delete",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "message": {},
            "uploadedFilesCount": {}
        };
    }
    static get events() {
        return [{
                "method": "uxFileChange",
                "name": "uxFileChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when files are added or removed"
                },
                "complexType": {
                    "original": "{\n        files: File[];\n    }",
                    "resolved": "{ files: File[]; }",
                    "references": {
                        "File": {
                            "location": "global",
                            "id": "global::File"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "reset": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Remove all files and reset inputs",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "element"; }
    static get listeners() {
        return [{
                "name": "uxFileUploadDelete",
                "method": "deleteInput",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "focusout",
                "method": "resetErrorOnBlur",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=file-upload.js.map
